// src/pages/MFAPage.jsx — MFA Audit for IMA Helpdesk Portal
// Reads:  /beta/reports/authenticationMethods/userRegistrationDetails (bulk, no per-user calls)
// Writes: via /api/set-mfa-state Azure Function (app credentials, server-side)

import React, { useState, useEffect, useCallback } from "react";
import { graphRequest } from "../services/graphService";
import { GRAPH_BETA } from "../authConfig";

const PAGE_SIZE = 50;

// Keys are exact strings from the reporting endpoint (methodsRegistered + defaultMfaMethod)
const METHOD_META = {
  "microsoftAuthenticatorPush":          { label: "Auth App",     color: "#00B2E3" },
  "microsoftAuthenticatorPasswordless":  { label: "Auth App (PL)", color: "#00B2E3" },
  "microsoftAuthenticator":              { label: "Auth App",     color: "#00B2E3" },
  "authenticatorApp":                    { label: "Auth App",     color: "#00B2E3" },
  "softwareOath":                        { label: "Auth App",     color: "#00B2E3" },
  "mobilePhone":                         { label: "SMS / Voice",  color: "#00BBB4" },
  "alternateMobilePhone":                { label: "Alt Phone",    color: "#00BBB4" },
  "officePhone":                         { label: "Office Ph.",   color: "#00BBB4" },
  "phoneNumber":                         { label: "SMS / Voice",  color: "#00BBB4" },
  "voice":                               { label: "Voice Call",   color: "#00BBB4" },
  "fido2SecurityKey":                    { label: "FIDO2 Key",    color: "#FF9E18" },
  "fido":                                { label: "FIDO2 Key",    color: "#FF9E18" },
  "passKeyDeviceBound":                  { label: "Passkey",      color: "#FF9E18" },
  "passKeyDeviceBoundAuthenticator":     { label: "Passkey",      color: "#FF9E18" },
  "email":                               { label: "Email OTP",    color: "#8b7ef8" },
  "emailOtp":                            { label: "Email OTP",    color: "#8b7ef8" },
  "temporaryAccessPass":                 { label: "Temp Pass",    color: "#e05252" },
  "windowsHelloForBusiness":             { label: "Win Hello",    color: "#6ABF4B" },
  "softwareOneTimePasscode":             { label: "OATH Token",   color: "#3F738D" },
  "hardwareOneTimePasscode":             { label: "HW OATH",      color: "#3F738D" },
  "hardwareOath":                        { label: "HW OATH",      color: "#3F738D" },
  "externalAuthMethod":                  { label: "External MFA", color: "#7A97AB" },
  "thirdPartyProvider":                  { label: "External MFA", color: "#7A97AB" },
};

function methodLabel(key) {
  if (!key || key === "none") return "—";
  if (METHOD_META[key]) return METHOD_META[key].label;
  return key.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase()).trim();
}
function methodColor(key) { return METHOD_META[key]?.color || "#7A97AB"; }

// MFA state display
const STATE_META = {
  enabled:  { label: "Enabled",  color: "var(--orange)", dim: "var(--orange-dim)", border: "rgba(255,158,24,0.3)" },
  enforced: { label: "Enforced", color: "var(--green)",  dim: "var(--green-dim)",  border: "rgba(106,191,75,0.3)" },
  disabled: { label: "Disabled", color: "var(--red)",    dim: "var(--red-dim)",    border: "rgba(224,82,82,0.3)"  },
};

// ── API call to our Azure Function ─────────────────────────────────────────────
const FUNCTIONS_BASE = "https://ima-response-functions-fud2d0cwhechfyf3.centralus-01.azurewebsites.net/api";

async function callSetMFAState(userIds, state, requestedBy) {
  const res = await fetch(`${FUNCTIONS_BASE}/set-mfa-state?code=ZCY9M0H6sfQ5ENLoiNH_SMDE-MP5-QGiSgYgA7qFkOSWAzFutZ7TLA==`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userIds, state, requestedBy }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

// ── Fetch all registration details ─────────────────────────────────────────────
async function fetchAllRegistrationDetails() {
  let results = [];
  let url = `${GRAPH_BETA}/reports/authenticationMethods/userRegistrationDetails?$top=999`;
  while (url) {
    const data = await graphRequest(url);
    results = results.concat(data?.value || []);
    url = data?.["@odata.nextLink"] || null;
  }
  return results;
}

// ── Sub-components ─────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{ background: "var(--s1)", border: "1px solid var(--b1)", borderTop: `3px solid ${accent}`, borderRadius: "var(--r)", padding: "12px 14px" }}>
      <div style={{ fontSize: 10, color: "var(--tx3)", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 900, color: "var(--tx)", lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "var(--tx3)", marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function MethodTag({ methodKey }) {
  const color = methodColor(methodKey);
  return (
    <span style={{ background: color + "22", color, border: `1px solid ${color}44`, borderRadius: 4, padding: "1px 5px", fontSize: 10, fontWeight: 500, whiteSpace: "nowrap" }}>
      {methodLabel(methodKey)}
    </span>
  );
}

function RegistrationPill({ registered }) {
  return registered ? (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "var(--green-dim)", color: "var(--green)", borderRadius: 99, padding: "2px 8px", fontSize: 10, fontWeight: 600, whiteSpace: "nowrap" }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--green)" }}/> Registered
    </span>
  ) : (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "var(--red-dim)", color: "var(--red)", borderRadius: 99, padding: "2px 8px", fontSize: 10, fontWeight: 600, whiteSpace: "nowrap" }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--red)" }}/> Not Registered
    </span>
  );
}

function MFAStatePill({ state }) {
  const m = STATE_META[state] || STATE_META.disabled;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: m.dim, color: m.color, border: `1px solid ${m.border}`, borderRadius: 99, padding: "2px 8px", fontSize: 10, fontWeight: 600, whiteSpace: "nowrap" }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: m.color }}/>{m.label}
    </span>
  );
}

function SortTh({ col, label, sortCol, sortDir, onSort, style }) {
  const active = sortCol === col;
  return (
    <th onClick={() => onSort(col)} style={{ ...style, cursor: "pointer", userSelect: "none", color: active ? "var(--primary)" : "var(--tx3)" }}>
      {label} {active ? (sortDir === "asc" ? "↑" : "↓") : <span style={{ opacity: 0.3 }}>↕</span>}
    </th>
  );
}

// Confirmation modal (in-flow, not fixed — won't collapse iframe)
function ConfirmModal({ users, action, onConfirm, onCancel, busy }) {
  const m = STATE_META[action];
  const count = users.length;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(7,21,32,0.75)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--s1)", border: "1px solid var(--b2)", borderRadius: 12, padding: "24px 28px", width: 400, animation: "fadeIn .15s ease" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--tx)", marginBottom: 10 }}>
          {action === "enabled" ? "Enable" : action === "enforced" ? "Enforce" : "Disable"} MFA
          {count > 1 ? ` — ${count} users` : ""}
        </div>
        <div style={{ fontSize: 12, color: "var(--tx2)", marginBottom: 6, lineHeight: 1.7 }}>
          {action === "disabled" && (
            <div style={{ background: "var(--red-dim)", border: "1px solid rgba(224,82,82,0.3)", borderRadius: "var(--r)", padding: "8px 12px", marginBottom: 10, fontSize: 11, color: "var(--red)" }}>
              ⚠ Disabling MFA reduces account security. Ensure this is an approved change.
            </div>
          )}
          This will set per-user MFA state to <strong style={{ color: m.color }}>{m.label}</strong> for:
        </div>
        <div style={{ background: "var(--s2)", borderRadius: "var(--r)", padding: "8px 12px", marginBottom: 16, maxHeight: 120, overflowY: "auto" }}>
          {users.slice(0, 10).map(u => (
            <div key={u.id} style={{ fontSize: 11, color: "var(--tx2)", padding: "2px 0" }}>{u.displayName} <span style={{ color: "var(--tx3)" }}>({u.mail})</span></div>
          ))}
          {users.length > 10 && <div style={{ fontSize: 11, color: "var(--tx3)", marginTop: 4 }}>…and {users.length - 10} more</div>}
        </div>
        <div style={{ fontSize: 11, color: "var(--tx3)", marginBottom: 16 }}>
          Note: This sets the legacy per-user MFA toggle. If your tenant enforces MFA via Conditional Access, this setting may be overridden by CA policy.
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel} disabled={busy} style={{ background: "transparent", border: "1px solid var(--b2)", borderRadius: "var(--r)", color: "var(--tx3)", fontFamily: "var(--sans)", fontSize: 12, padding: "7px 16px", cursor: "pointer" }}>Cancel</button>
          <button onClick={onConfirm} disabled={busy} style={{ background: m.color === "var(--red)" ? "var(--red)" : m.color === "var(--green)" ? "var(--green)" : "var(--orange)", border: "none", borderRadius: "var(--r)", color: "#fff", fontFamily: "var(--sans)", fontSize: 12, fontWeight: 600, padding: "7px 16px", cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1, display: "flex", alignItems: "center", gap: 6 }}>
            {busy && <span style={{ width: 12, height: 12, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin .7s linear infinite" }}/>}
            {busy ? "Applying…" : `${action === "enabled" ? "Enable" : action === "enforced" ? "Enforce" : "Disable"} MFA`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
export function MFAPage({ user: currentUser }) {
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [view,     setView]     = useState("overview");
  const [search,   setSearch]   = useState("");
  const [sortCol,  setSortCol]  = useState("name");
  const [sortDir,  setSortDir]  = useState("asc");
  const [page,     setPage]     = useState(0);
  const [detail,   setDetail]   = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [modal,    setModal]    = useState(null); // { users: [], action: "enabled"|"disabled"|"enforced" }
  const [busy,     setBusy]     = useState(false);
  const [toast,    setToast]    = useState("");
  const toastTimer = React.useRef(null);

  function showToast(msg) {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 4000);
  }

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    setUsers([]);
    setDetail(null);
    setSelected(new Set());
    try {
      const records = await fetchAllRegistrationDetails();
      const mapped = records
        .filter(r => r.userType === "member" || !r.userType)
        .map(r => ({
          id:              r.id,
          displayName:     r.userDisplayName || r.userPrincipalName || "",
          mail:            r.userPrincipalName || "",
          isMfaRegistered: r.isMfaRegistered || false,
          isMfaCapable:    r.isMfaCapable || false,
          isAdmin:         r.isAdmin || false,
          defaultMethod:   r.defaultMfaMethod || null,
          methods:         r.methodsRegistered || [],
          // perUserMfaState may not be in this endpoint — we track it locally after writes
          mfaState:        r.perUserMfaState || null,
        }));
      setUsers(mapped);
    } catch (err) {
      setError(err.message || "Failed to load MFA data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── MFA state change ───────────────────────────────────────────────────────
  async function applyMFAState(targetUsers, newState) {
    setBusy(true);
    try {
      const result = await callSetMFAState(
        targetUsers.map(u => u.id),
        newState,
        currentUser?.mail || "unknown"
      );
      // Optimistically update local state for succeeded users
      const succeededIds = new Set(result.results.filter(r => r.success).map(r => r.userId));
      setUsers(prev => prev.map(u =>
        succeededIds.has(u.id) ? { ...u, mfaState: newState } : u
      ));
      if (detail && succeededIds.has(detail.id)) {
        setDetail(d => ({ ...d, mfaState: newState }));
      }
      setSelected(new Set());
      setModal(null);
      if (result.failed === 0) {
        showToast(`✓ MFA ${newState} for ${result.succeeded} user${result.succeeded !== 1 ? "s" : ""}`);
      } else {
        showToast(`⚠ ${result.succeeded} succeeded, ${result.failed} failed`);
        setError(`${result.failed} user(s) failed: ${result.results.filter(r => !r.success).map(r => r.error).join(", ")}`);
      }
    } catch (err) {
      setModal(null);
      setError(`Failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  function openModal(targetUsers, action) {
    setModal({ users: targetUsers, action });
  }

  // ── Export ─────────────────────────────────────────────────────────────────
  function doExport() {
    const rows = sortedFiltered;
    const csv = [
      "Name,Email,MFA Registered,MFA State,Default Method,Methods,Is Admin",
      ...rows.map(u =>
        `"${u.displayName}","${u.mail}",${u.isMfaRegistered},${u.mfaState || "unknown"},"${methodLabel(u.defaultMethod)}","${u.methods.map(methodLabel).join("; ")}",${u.isAdmin}`
      ),
    ].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `mfa-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  }

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
    setPage(0);
  }

  function toggleSelect(id) {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function togglePageAll(checked) {
    setSelected(prev => {
      const n = new Set(prev);
      pageRows.forEach(u => checked ? n.add(u.id) : n.delete(u.id));
      return n;
    });
  }

  // ── Derived stats ──────────────────────────────────────────────────────────
  const total       = users.length;
  const registered  = users.filter(u => u.isMfaRegistered).length;
  const unreg       = users.filter(u => !u.isMfaRegistered).length;
  const coveragePct = total > 0 ? Math.round(registered / total * 100) : 0;
  const appCount    = users.filter(u => u.methods.some(m => ["microsoftAuthenticatorPush","microsoftAuthenticatorPasswordless","microsoftAuthenticator","authenticatorApp","softwareOath"].includes(m))).length;
  const fido2Count  = users.filter(u => u.methods.some(m => ["fido2SecurityKey","fido","passKeyDeviceBound","passKeyDeviceBoundAuthenticator"].includes(m))).length;

  const methodFreq = {};
  users.forEach(u => u.methods.forEach(m => { methodFreq[m] = (methodFreq[m] || 0) + 1; }));
  const topMethods = Object.entries(methodFreq).sort((a, b) => b[1] - a[1]).slice(0, 8);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchQ = !q || u.displayName.toLowerCase().includes(q) || u.mail.toLowerCase().includes(q);
    const matchV =
      view === "overview" ? true :
      view === "none"     ? !u.isMfaRegistered :
      view === "reg"      ? u.isMfaRegistered  : true;
    return matchQ && matchV;
  });

  const sortedFiltered = [...filtered].sort((a, b) => {
    let av, bv;
    if      (sortCol === "name")    { av = a.displayName.toLowerCase(); bv = b.displayName.toLowerCase(); }
    else if (sortCol === "status")  { av = a.isMfaRegistered ? 1 : 0;  bv = b.isMfaRegistered ? 1 : 0; }
    else if (sortCol === "count")   { av = a.methods.length;            bv = b.methods.length; }
    else if (sortCol === "default") { av = a.defaultMethod || "";       bv = b.defaultMethod || ""; }
    else                            { av = a.mail.toLowerCase();        bv = b.mail.toLowerCase(); }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ?  1 : -1;
    return 0;
  });

  const totalPages   = Math.max(1, Math.ceil(sortedFiltered.length / PAGE_SIZE));
  const pageRows     = sortedFiltered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const allPageSel   = pageRows.length > 0 && pageRows.every(u => selected.has(u.id));
  const selectedList = users.filter(u => selected.has(u.id));

  const subTab = id => ({
    background: view === id ? "var(--s3)" : "transparent",
    border: "1px solid " + (view === id ? "var(--b2)" : "transparent"),
    color: view === id ? "var(--tx)" : "var(--tx3)",
    fontFamily: "var(--sans)", fontSize: 11, fontWeight: view === id ? 600 : 400,
    borderRadius: "var(--r)", padding: "4px 12px", cursor: "pointer",
  });
  const thBase = { padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 600, borderBottom: "1px solid var(--b1)", background: "var(--s2)", whiteSpace: "nowrap" };
  const td     = { padding: "8px 10px", fontSize: 12, color: "var(--tx2)", borderBottom: "1px solid var(--b1)", verticalAlign: "middle" };

  const actionBtn = (label, color, dim, border, onClick) => (
    <button onClick={onClick} style={{ background: dim, border: `1px solid ${border}`, borderRadius: "var(--r)", color, fontFamily: "var(--sans)", fontSize: 10, padding: "3px 8px", cursor: "pointer", whiteSpace: "nowrap" }}>
      {label}
    </button>
  );

  return (
    <div style={{ height: "100%", overflow: "auto", padding: "16px 20px", fontFamily: "var(--sans)" }}>

      {/* Modal */}
      {modal && (
        <ConfirmModal
          users={modal.users}
          action={modal.action}
          busy={busy}
          onCancel={() => !busy && setModal(null)}
          onConfirm={() => applyMFAState(modal.users, modal.action)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 200, background: "var(--s2)", border: "1px solid var(--b2)", borderRadius: "var(--r)", padding: "10px 16px", fontSize: 12, color: "var(--tx)", animation: "fadeIn .2s ease" }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--tx)" }}>🔐 MFA Audit</span>
          <span style={{ background: "var(--primary-dim)", color: "var(--primary)", borderRadius: 99, padding: "1px 8px", fontSize: 10, fontWeight: 600 }}>{total} users</span>
          {loading && <span style={{ fontSize: 10, color: "var(--tx3)", display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 10, border: "1.5px solid var(--b3)", borderTopColor: "var(--primary)", borderRadius: "50%", display: "inline-block", animation: "spin .7s linear infinite" }}/> Loading…</span>}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={doExport} disabled={loading || total === 0} style={{ background: "transparent", border: "1px solid var(--b2)", borderRadius: "var(--r)", color: "var(--tx3)", fontFamily: "var(--sans)", fontSize: 11, padding: "5px 11px", cursor: "pointer", opacity: (loading || total === 0) ? 0.5 : 1 }}>↓ Export CSV</button>
          <button onClick={loadData} disabled={loading} style={{ background: "var(--primary)", border: "none", borderRadius: "var(--r)", color: "#fff", fontFamily: "var(--sans)", fontSize: 11, fontWeight: 600, padding: "5px 12px", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}>
            {loading ? "Loading…" : "↻ Refresh"}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: "var(--red-dim)", border: "1px solid rgba(224,82,82,0.3)", borderRadius: "var(--r)", padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "var(--red)" }}>
          ⚠ {error}
          <button onClick={() => setError("")} style={{ marginLeft: 10, background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontSize: 14 }}>✕</button>
        </div>
      )}

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 10 }}>
        <StatCard label="Total Users"       value={total}       sub="in directory"           accent="var(--primary)" />
        <StatCard label="MFA Registered"    value={registered}  sub={`${coveragePct}% coverage`} accent="var(--green)" />
        <StatCard label="Not Registered"    value={unreg}       sub="no methods on file"     accent="var(--red)" />
        <StatCard label="Authenticator App" value={appCount}    sub="strongest method"       accent="var(--orange)" />
        <StatCard label="FIDO2 / Passkey"   value={fido2Count}  sub="phishing resistant"     accent="var(--purple)" />
      </div>

      {/* Coverage bar */}
      <div style={{ background: "var(--s1)", border: "1px solid var(--b1)", borderRadius: "var(--r)", padding: "10px 14px", display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <span style={{ fontSize: 11, color: "var(--tx3)", whiteSpace: "nowrap" }}>MFA Coverage</span>
        <div style={{ flex: 1, background: "var(--s3)", borderRadius: 4, height: 8, overflow: "hidden" }}>
          <div style={{ width: `${coveragePct}%`, height: "100%", borderRadius: 4, background: "linear-gradient(90deg, var(--ima-dark), var(--primary))", transition: "width .5s ease" }}/>
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--tx)", whiteSpace: "nowrap" }}>{coveragePct}%</span>
        <span style={{ fontSize: 11, color: "var(--tx3)", whiteSpace: "nowrap" }}>{registered} of {total} users</span>
      </div>

      {/* Method frequency */}
      {topMethods.length > 0 && (
        <div style={{ background: "var(--s1)", border: "1px solid var(--b1)", borderRadius: "var(--r)", padding: "12px 14px", marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--tx2)", marginBottom: 10 }}>Method adoption — {total} users</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {topMethods.map(([key, count]) => {
              const pct = total > 0 ? Math.round(count / total * 100) : 0;
              const color = methodColor(key);
              return (
                <div key={key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 120, fontSize: 11, color: "var(--tx2)", textAlign: "right", flexShrink: 0 }}>{methodLabel(key)}</div>
                  <div style={{ flex: 1, background: "var(--s3)", borderRadius: 4, height: 16, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 4, display: "flex", alignItems: "center", paddingLeft: 6, minWidth: count > 0 ? 24 : 0, transition: "width .4s" }}>
                      {pct > 3 && <span style={{ fontSize: 9, color: "#fff", fontFamily: "var(--mono)" }}>{count}</span>}
                    </div>
                  </div>
                  <div style={{ width: 36, fontFamily: "var(--mono)", fontSize: 11, color: "var(--tx3)", textAlign: "right" }}>{count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div style={{ background: "var(--s1)", border: "1px solid var(--b1)", borderRadius: "var(--r)", padding: "8px 12px", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <button style={subTab("overview")} onClick={() => { setView("overview"); setPage(0); }}>All ({total})</button>
          <button style={subTab("none")}     onClick={() => { setView("none");     setPage(0); }}>Not Registered ({unreg})</button>
          <button style={subTab("reg")}      onClick={() => { setView("reg");      setPage(0); }}>Registered ({registered})</button>
          <div style={{ width: 1, height: 20, background: "var(--b2)", margin: "0 4px" }}/>
          <div style={{ position: "relative" }}>
            <input type="text" placeholder="Search name or email…" value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              style={{ background: "var(--s2)", border: "1px solid var(--b2)", borderRadius: "var(--r)", padding: "4px 28px 4px 10px", fontSize: 11, color: "var(--tx)", fontFamily: "var(--sans)", outline: "none", width: 220 }}/>
            {search && <button onClick={() => { setSearch(""); setPage(0); }} style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--tx3)", cursor: "pointer", fontSize: 14, lineHeight: 1 }}>✕</button>}
          </div>
          <span style={{ fontSize: 11, color: "var(--tx3)", marginLeft: "auto" }}>{sortedFiltered.length} results</span>
        </div>

        {/* Bulk action bar — appears when rows are selected */}
        {selected.size > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--b1)", flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--tx2)" }}>{selected.size} user{selected.size !== 1 ? "s" : ""} selected</span>
            <span style={{ fontSize: 11, color: "var(--tx3)" }}>Set MFA state:</span>
            {actionBtn("Enable",  "var(--orange)", "var(--orange-dim)", "rgba(255,158,24,0.3)", () => openModal(selectedList, "enabled"))}
            {actionBtn("Enforce", "var(--green)",  "var(--green-dim)",  "rgba(106,191,75,0.3)", () => openModal(selectedList, "enforced"))}
            {actionBtn("Disable", "var(--red)",    "var(--red-dim)",    "rgba(224,82,82,0.3)",  () => openModal(selectedList, "disabled"))}
            <button onClick={() => setSelected(new Set())} style={{ background: "transparent", border: "none", color: "var(--tx3)", fontFamily: "var(--sans)", fontSize: 11, cursor: "pointer", marginLeft: 4 }}>Clear</button>
          </div>
        )}
      </div>

      {/* Table + detail */}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ background: "var(--s1)", border: "1px solid var(--b1)", borderRadius: "var(--r)", overflow: "hidden" }}>
            {loading ? (
              <div style={{ padding: 60, textAlign: "center", color: "var(--tx3)", fontSize: 12 }}>
                <div style={{ width: 20, height: 20, border: "2px solid var(--b3)", borderTopColor: "var(--primary)", borderRadius: "50%", animation: "spin .7s linear infinite", margin: "0 auto 12px" }}/>
                Fetching MFA registration data from Microsoft Graph…
                <div style={{ marginTop: 8, fontSize: 11 }}>This is a single bulk call — should take a few seconds.</div>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: 700 }}>
                  <colgroup>
                    <col style={{ width: 36 }}/>
                    <col style={{ width: "26%" }}/>
                    <col style={{ width: 115 }}/>
                    <col/>
                    <col style={{ width: 115 }}/>
                    <col style={{ width: 170 }}/>
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={thBase}>
                        <input type="checkbox" checked={allPageSel} onChange={e => togglePageAll(e.target.checked)}/>
                      </th>
                      <SortTh col="name"    label="Name / Email"       sortCol={sortCol} sortDir={sortDir} onSort={handleSort} style={thBase}/>
                      <SortTh col="status"  label="Registered"         sortCol={sortCol} sortDir={sortDir} onSort={handleSort} style={thBase}/>
                      <SortTh col="count"   label="Methods"            sortCol={sortCol} sortDir={sortDir} onSort={handleSort} style={thBase}/>
                      <SortTh col="default" label="Default"            sortCol={sortCol} sortDir={sortDir} onSort={handleSort} style={thBase}/>
                      <th style={{ ...thBase, textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(u => {
                      const isSel    = selected.has(u.id);
                      const isDetail = detail?.id === u.id;
                      return (
                        <tr key={u.id} onClick={() => setDetail(isDetail ? null : u)}
                          style={{ cursor: "pointer", background: isDetail ? "var(--s3)" : isSel ? "rgba(0,178,227,0.05)" : "transparent" }}>
                          <td style={td} onClick={e => { e.stopPropagation(); toggleSelect(u.id); }}>
                            <input type="checkbox" checked={isSel} onChange={() => toggleSelect(u.id)}/>
                          </td>
                          <td style={td}>
                            <div style={{ fontWeight: 600, color: "var(--tx)", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {u.displayName}
                              {u.isAdmin && <span style={{ marginLeft: 5, fontSize: 9, background: "var(--orange-dim)", color: "var(--orange)", border: "1px solid rgba(255,158,24,0.3)", borderRadius: 3, padding: "1px 4px" }}>Admin</span>}
                            </div>
                            <div style={{ fontSize: 10, color: "var(--tx3)", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.mail}</div>
                          </td>
                          <td style={td}><RegistrationPill registered={u.isMfaRegistered}/></td>
                          <td style={{ ...td, overflow: "hidden" }}>
                            {u.methods.length === 0 ? (
                              <span style={{ fontSize: 10, color: "var(--tx3)" }}>None</span>
                            ) : (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                                {u.methods.slice(0, 3).map((m, i) => <MethodTag key={i} methodKey={m}/>)}
                                {u.methods.length > 3 && <span style={{ fontSize: 10, color: "var(--tx3)" }}>+{u.methods.length - 3}</span>}
                              </div>
                            )}
                          </td>
                          <td style={{ ...td, fontSize: 11 }}>
                            {u.mfaState ? <MFAStatePill state={u.mfaState}/> : u.defaultMethod ? <span style={{ color: "var(--tx)" }}>{methodLabel(u.defaultMethod)}</span> : <span style={{ color: "var(--tx3)" }}>—</span>}
                          </td>
                          <td style={{ ...td, textAlign: "right" }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: "flex", gap: 4, justifyContent: "flex-end", flexWrap: "wrap" }}>
                              {actionBtn("Enable",  "var(--orange)", "var(--orange-dim)", "rgba(255,158,24,0.3)", () => openModal([u], "enabled"))}
                              {actionBtn("Enforce", "var(--green)",  "var(--green-dim)",  "rgba(106,191,75,0.3)", () => openModal([u], "enforced"))}
                              {actionBtn("Disable", "var(--red)",    "var(--red-dim)",    "rgba(224,82,82,0.3)",  () => openModal([u], "disabled"))}
                              <button onClick={() => setDetail(isDetail ? null : u)}
                                style={{ background: "var(--s3)", border: "1px solid var(--b2)", borderRadius: "var(--r)", color: "var(--tx3)", fontFamily: "var(--sans)", fontSize: 10, padding: "3px 8px", cursor: "pointer" }}>
                                {isDetail ? "✕" : "···"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {pageRows.length === 0 && !loading && (
                      <tr><td colSpan={6} style={{ ...td, textAlign: "center", color: "var(--tx3)", padding: 32 }}>
                        {search ? `No users matching "${search}"` : "No users in this view"}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, fontSize: 11, color: "var(--tx3)" }}>
              <span>{sortedFiltered.length} users · page {page + 1} of {totalPages}</span>
              <div style={{ display: "flex", gap: 4 }}>
                <button disabled={page===0} onClick={() => setPage(0)} style={{ background: "var(--s2)", border: "1px solid var(--b1)", borderRadius: "var(--r)", padding: "3px 8px", cursor: page===0?"not-allowed":"pointer", color: "var(--tx3)", fontFamily: "var(--sans)", fontSize: 11, opacity: page===0?0.4:1 }}>«</button>
                <button disabled={page===0} onClick={() => setPage(p=>p-1)} style={{ background: "var(--s2)", border: "1px solid var(--b1)", borderRadius: "var(--r)", padding: "3px 8px", cursor: page===0?"not-allowed":"pointer", color: "var(--tx3)", fontFamily: "var(--sans)", fontSize: 11, opacity: page===0?0.4:1 }}>‹</button>
                {Array.from({ length: Math.min(totalPages, 9) }, (_, i) => {
                  const p = totalPages <= 9 ? i : Math.max(0, Math.min(page-4, totalPages-9)) + i;
                  return <button key={p} onClick={() => setPage(p)} style={{ background: page===p?"var(--primary)":"var(--s2)", border: "1px solid "+(page===p?"var(--primary)":"var(--b1)"), borderRadius: "var(--r)", padding: "3px 8px", cursor: "pointer", color: page===p?"#fff":"var(--tx3)", fontFamily: "var(--sans)", fontSize: 11 }}>{p+1}</button>;
                })}
                <button disabled={page>=totalPages-1} onClick={() => setPage(p=>p+1)} style={{ background: "var(--s2)", border: "1px solid var(--b1)", borderRadius: "var(--r)", padding: "3px 8px", cursor: page>=totalPages-1?"not-allowed":"pointer", color: "var(--tx3)", fontFamily: "var(--sans)", fontSize: 11, opacity: page>=totalPages-1?0.4:1 }}>›</button>
                <button disabled={page>=totalPages-1} onClick={() => setPage(totalPages-1)} style={{ background: "var(--s2)", border: "1px solid var(--b1)", borderRadius: "var(--r)", padding: "3px 8px", cursor: page>=totalPages-1?"not-allowed":"pointer", color: "var(--tx3)", fontFamily: "var(--sans)", fontSize: 11, opacity: page>=totalPages-1?0.4:1 }}>»</button>
              </div>
            </div>
          )}
        </div>

        {/* Detail panel */}
        {detail && (
          <div style={{ width: 270, flexShrink: 0, background: "var(--s1)", border: "1px solid var(--b1)", borderRadius: "var(--r)", padding: "14px", animation: "fadeIn .15s ease" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--tx)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{detail.displayName}</div>
                <div style={{ fontSize: 10, color: "var(--tx3)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{detail.mail}</div>
              </div>
              <button onClick={() => setDetail(null)} style={{ background: "transparent", border: "none", color: "var(--tx3)", cursor: "pointer", fontSize: 14, padding: 0, flexShrink: 0, marginLeft: 8 }}>✕</button>
            </div>

            {/* Status + MFA state actions */}
            <div style={{ background: "var(--s2)", borderRadius: "var(--r)", padding: "10px 12px", marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "var(--tx3)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>MFA Status</div>
              <RegistrationPill registered={detail.isMfaRegistered}/>
              {detail.mfaState && <div style={{ marginTop: 6 }}><MFAStatePill state={detail.mfaState}/></div>}
              {detail.isMfaCapable && !detail.isMfaRegistered && (
                <div style={{ marginTop: 6, fontSize: 10, color: "var(--orange)" }}>⚠ Capable but not registered</div>
              )}
              {detail.defaultMethod && (
                <div style={{ marginTop: 8, fontSize: 11, color: "var(--tx2)" }}>Default: <strong style={{ color: "var(--tx)" }}>{methodLabel(detail.defaultMethod)}</strong></div>
              )}

              {/* Per-user MFA state buttons */}
              <div style={{ marginTop: 10, fontSize: 10, fontWeight: 600, color: "var(--tx3)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Set MFA State</div>
              <div style={{ display: "flex", gap: 4 }}>
                {actionBtn("Enable",  "var(--orange)", "var(--orange-dim)", "rgba(255,158,24,0.3)", () => openModal([detail], "enabled"))}
                {actionBtn("Enforce", "var(--green)",  "var(--green-dim)",  "rgba(106,191,75,0.3)", () => openModal([detail], "enforced"))}
                {actionBtn("Disable", "var(--red)",    "var(--red-dim)",    "rgba(224,82,82,0.3)",  () => openModal([detail], "disabled"))}
              </div>

              <div style={{ marginTop: 10 }}>
                <a href={`https://entra.microsoft.com/#view/Microsoft_AAD_UsersAndTenants/UserProfileMenuBlade/~/UserAuthMethods/userId/${detail.id}`}
                  target="_blank" rel="noreferrer"
                  style={{ display: "block", textAlign: "center", background: "var(--s3)", border: "1px solid var(--b2)", borderRadius: "var(--r)", color: "var(--tx2)", fontFamily: "var(--sans)", fontSize: 11, padding: "6px 0", textDecoration: "none" }}>
                  Manage in Entra ID ↗
                </a>
              </div>
            </div>

            {/* Methods list */}
            <div style={{ background: "var(--s2)", borderRadius: "var(--r)", padding: "10px 12px" }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "var(--tx3)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Registered Methods ({detail.methods.length})
              </div>
              {detail.methods.length === 0 ? (
                <div style={{ fontSize: 11, color: "var(--tx3)" }}>None registered</div>
              ) : detail.methods.map((m, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0", borderBottom: i < detail.methods.length - 1 ? "1px solid var(--b1)" : "none" }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: methodColor(m), flexShrink: 0 }}/>
                  <span style={{ fontSize: 11, color: "var(--tx)" }}>{methodLabel(m)}</span>
                  {m === detail.defaultMethod && <span style={{ fontSize: 9, background: "var(--primary-dim)", color: "var(--primary)", borderRadius: 3, padding: "1px 4px", marginLeft: "auto" }}>default</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
